<template>
  <ODialog
    ref="dialog"
    :title="$t('components.service.actions.removeAutomation.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'danger', callback: confirm },
    ]"
    :portal="portal"
  />
</template>

<script setup lang="ts">
import type { Service } from '~/_api/models/service'

interface ServiceAutomation {
  contentTemplate?: {
    name: string
    idContentTemplate: number
  }
}

interface Props {
  service: Service
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false
})

const emit = defineEmits<Emits>()
const { $i18n, $notification, $api } = useNuxtApp()

const dialog = ref()
const serviceAutomation = ref<ServiceAutomation | null>(null)

const info = computed(() => {
  return $i18n.t('components.service.actions.removeAutomation.info', { 
    title: serviceAutomation.value?.contentTemplate?.name, 
    id: serviceAutomation.value?.contentTemplate?.idContentTemplate 
  })
})

const open = (automation: ServiceAutomation) => {
  serviceAutomation.value = automation
  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.removeAutomation(props.service, serviceAutomation.value)
    const service = response.data
    emit('update:loading', false)

    if (service) {
      $notification.toast({
        title: $i18n.t('components.service.actions.removeAutomation.success'),
        text: $i18n.t('components.service.actions.removeAutomation.successInfo', { title: serviceAutomation.value?.contentTemplate?.name }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)

      return true
    }
  } catch (e) {
    console.log(e)
    $notification.toast({
      title: $i18n.t('components.service.actions.removeAutomation.error'),
      text: $i18n.t('components.service.actions.removeAutomation.errorInfo', { title: serviceAutomation.value?.contentTemplate?.name }),
      variant: 'danger',
    })

    emit('error')
  }
}

defineExpose({
  open
})
</script>
